import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import baseTheme from '@cobalt/theme-base';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function styleInject(css, ref) {
  if (ref === void 0) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') {
    return;
  }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css = "@import url(https://prd-cdn-talkdesk.talkdesk.com/cobalt-utils/cobalt-fonts/font-face.css);";
styleInject(css);

function getThemeProperties(themeObject) {
  return Object.entries(themeObject).reduce((acc, [k, {
    css,
    value
  }]) => {
    return {
      styles: { ...acc.styles,
        [css]: value
      },
      theme: { ...acc.theme,
        [k]: value
      }
    };
  }, {
    styles: {},
    theme: {}
  });
}

const ThemeContext = /*#__PURE__*/createContext(null);

const defaultThemeLoader = () => Promise.resolve(baseTheme);

const fontFaceUrl = "cobalt-utils/cobalt-fonts/font-face.css";
const flagsSpriteUrl = "cobalt/assets/1.0.0/flags/sprite.svg";
const defaultBaseCdnUrl = "https://prd-cdn-talkdesk.talkdesk.com";

function useTheme() {
  const theme = useContext(ThemeContext);

  if (!theme) {
    throw new Error("You must use a ThemeProvider to get a theme.");
  }

  return theme;
}

const providerStyles = {
  height: "100%"
};

const ThemeProvider = ({
  children,
  placeholder,
  loader,
  onLoad,
  onError,
  useFallback = true,
  style = {},
  ...props
}) => {
  const [theme, setTheme] = useState(null);
  const isMounted = useRef(false);
  const themeLoader = loader || defaultThemeLoader;
  useEffect(() => {
    var _theme$cdnBaseUrl;

    if (!theme) return;
    const cdnUrl = ((_theme$cdnBaseUrl = theme.cdnBaseUrl) === null || _theme$cdnBaseUrl === void 0 ? void 0 : _theme$cdnBaseUrl.value) || defaultBaseCdnUrl;
    const alreadyExists = document.querySelector(`link[href='"${cdnUrl}/${fontFaceUrl}"']`);

    if (!alreadyExists) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.type = "text/css";
      link.href = `${cdnUrl}/${fontFaceUrl}`;
      document.head.appendChild(link);
    }
  }, [theme]);
  useEffect(() => {
    isMounted.current = true;
    themeLoader().then(data => {
      if (isMounted.current) {
        setTheme(data);
        onLoad && onLoad();
      }
    }).catch(() => {
      if (isMounted.current) {
        if (useFallback) {
          setTheme(baseTheme);
        }

        onError && onError();
      }
    });
    return () => {
      isMounted.current = false;
    };
  }, [themeLoader, useFallback, onLoad, onError]);

  if (!theme) {
    return placeholder ? /*#__PURE__*/React.createElement("div", {
      style: providerStyles
    }, placeholder) : null;
  }

  const mergedTheme = { ...baseTheme,
    ...theme
  };
  const {
    styles,
    theme: themeValue
  } = getThemeProperties(mergedTheme);
  const flagsSprite = theme.cdnBaseUrl ? {
    "--flags-sprite": `url(${theme.cdnBaseUrl.value}/${flagsSpriteUrl})`
  } : {};
  const customStyles = { ...flagsSprite,
    ...providerStyles,
    ...styles,
    ...style
  };
  return /*#__PURE__*/React.createElement(ThemeContext.Provider, {
    value: { ...themeValue
    }
  }, /*#__PURE__*/React.createElement("div", _extends({}, props, {
    style: customStyles
  }), children));
};

export default ThemeProvider;
export { ThemeContext, useTheme };
